import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLoginStore } from "../../../store/useLoginStore";
import { getEndpointConfiguration, request } from "../../../utils";
import { AlertBanner } from "../../../components/FormComponents/AlertBanner/AlertBanner";
import { TextInputField } from "../../../components/FormComponents/TextInputField/TextInputField";
import LoginOTPInput from "../LoginOTPInput";
import { LoginPageWrapper } from "../LoginPageWrapper";
import DragDropFileUpload from "./DragAndDropFileUpload";
import { REQUEST_OTP_NS } from "./i18n";

enum otpFormStates {
  Initial = 0,
  OtpField = 1,
  DropFile = 2,
}

const lightBgColor = "#D9EBFF";
const lightBorderColor = "#B8DAFF";
const darkBgColor = "#293542";
const darkBorderColor = "#34475C";

interface ResetOtpRequest {
  email: string;
  returnUrl: string | null;
}

export const RequestOTP = (props: {
  loginAction: (attachment?: File | null) => any;
  goBack: () => void;
  isOfflineRequest?: boolean;
}) => {
  const { loginAction, goBack, isOfflineRequest  } = props;

  const { palette } = useTheme();
  const { t } = useTranslation(REQUEST_OTP_NS);
  const { isCloud } = getEndpointConfiguration();

  const {
    loginHasFailed,
    loginData,
    isSubmitButtonLoading,
    validationErrors,
    setLoginHasFailed,
    updateLoginData,
  } = useLoginStore();

  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [formState, setFormState] = useState(otpFormStates.Initial);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const endpoints = getEndpointConfiguration();

  const dropFileButtonEnabled = endpoints.isCloud ?
    (uploadedFile && loginData.temporaryPassword?.length === 6)
    : uploadedFile;

  useEffect(() => {
    if (isOfflineRequest) {
      setLoginHasFailed(false);
      setFormState(otpFormStates["DropFile"]);
    }
  }, [isOfflineRequest, setLoginHasFailed])

  const handleRequestOTPButton = useCallback(() => {
    if (!loginData.email) {
      return;
    }
    
    setIsLoadingButton(true);
    
    const requestBody: ResetOtpRequest = {
      email: loginData.email,
      returnUrl: loginData.returnUrl
    };

    const requestNewOtp = async () => {
      try {
        await request.post<boolean>(
            endpoints.requestNewOtpEndpoint,
            { body: requestBody }
        );
      } catch (e) {
          console.log('OTP reset error:', e)
      } finally {
        setLoginHasFailed(false);
        setIsLoadingButton(false);
        setFormState(otpFormStates["OtpField"]);
      }
    };

    requestNewOtp();
  }, [endpoints, loginData, setLoginHasFailed]);

  const handleRequestDropFileForm = () => {
    if (!isSubmitButtonLoading) {
      setLoginHasFailed(false);
      setFormState(otpFormStates["DropFile"]);
    }
  };

  const handleBackButton = () => {
    setLoginHasFailed(false);

    if (isOfflineRequest) {
        setUploadedFile(null);
        goBack();
        return;
    }

    switch (formState) {
      case otpFormStates.Initial:
        goBack();
        return;
      case otpFormStates.OtpField:
        setFormState(otpFormStates.Initial);
        return;
      case otpFormStates.DropFile:
        setUploadedFile(null);
        setFormState(otpFormStates.OtpField);
        return;
    }
  };

  const requestForm = (
    <Box sx={{ width: "460px" }}>
      <Box sx={{ mb: 2, textAlign: "center" }}>
        <Typography
          sx={{
            mb: 0.25,
            fontWeight: "500",
          }}
        >
          {t("requestForm.title")}
        </Typography>
        <Typography sx={{ color: palette.text.secondary }}>
          {t("requestForm.subtitle")}
        </Typography>
      </Box>
      <TextInputField
        error={validationErrors.email}
        value={loginData.email ?? ''}
        onChange={evt => updateLoginData({ email: evt.target.value })}
        placeholder="Enter email address"
      />
      <Stack>
        <Button
          disabled={isLoadingButton || validationErrors.email}
          sx={{  flexGrow: 1 }}
          onClick={handleRequestOTPButton}
          variant="contained"
        >
            {isLoadingButton ? <CircularProgress size={18} /> : t("button.requestOtp") }
        </Button>
      </Stack>
    </Box>
  );

  const otpForm = (
    <Box sx={{ width: "460px" }}>
        {
          loginHasFailed && <AlertBanner
            sx={{ width: "460px" }}
            title="Sign-in failed"
            errorMessage={
              <Trans i18nKey="genericError">
                Please enter the One Time Password that has been sent to your email
              </Trans>
            }
          />
        }
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography
          sx={{
            mb: 0.25,
            fontWeight: "500",
          }}
        >
          {t("otpForm.title")}
        </Typography>
        <Typography sx={{ mb: 0.25, color: palette.text.secondary }}>
          {t("otpForm.subtitle")}
        </Typography>
        <Typography sx={{ color: palette.text.secondary }}>{loginData.email}</Typography>
      </Box>

      <LoginOTPInput
        value={loginData.otp}
        onValueChange={newOtp => {
          updateLoginData({ otp: newOtp });
          setLoginHasFailed(false)
        }}
        hasError={!!loginHasFailed}
        footerMessage={t("otpForm.otpInput.footerMessage")}
        lowerFooter={null}
      />

      <Stack>
        <Button
          disabled={loginData.otp?.length !== 6 || isSubmitButtonLoading}
          sx={{ backgroundColor: "#1D82C0", mt: 1.25, mb: 2 }}
          onClick={() => loginAction()}
          variant="contained"
        >
            {isSubmitButtonLoading ? <CircularProgress size={18} /> : t("button.guest")}
        </Button>
      </Stack>
      <Box
        sx={{
          p: 1,
          width: "460px",
          border: "1px solid",
          borderRadius: "4px",
          borderColor:
            palette.mode === "light" ? lightBorderColor : darkBorderColor,
          backgroundColor:
            palette.mode === "light" ? lightBgColor : darkBgColor,
          textAlign: "center",
        }}
      >
        <Typography sx={{ mb: 1 }}>{t("otpForm.footer.text")}</Typography>
        <Typography
          sx={{ color: "#3AA4E6", cursor: "pointer" }}
          onClick={handleRequestDropFileForm}
        >
          {t("otpForm.footer.button")}
        </Typography>
      </Box>
    </Box>
  );

  const dropFileFormCloud = (
    <>
      <Typography sx={{ fontSize: "14px" }} variant="h5">
        {t("dropFileForm.tempPassword.title")}
      </Typography>
      <Typography sx={{ color: palette.text.secondary, fontSize: "12px" }}>
        {t("dropFileForm.tempPassword.subtitle")}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <LoginOTPInput
          hasError={loginHasFailed}
          value={loginData.temporaryPassword ?? ''}
          onValueChange={val => {
            updateLoginData({ temporaryPassword: val });
            setLoginHasFailed(false);
          }}
          showTitle={false}
        />
      </Box>
      <Divider sx={{ my: 3 }} />
    </>
  );

  const dropFileForm = (
    <Box sx={{ width: "810px" }}>
        {loginHasFailed && <AlertBanner
            sx={{ width: "800px" }}
            title="Sign-in failed"
            errorMessage={
              <Trans i18nKey="genericError">
                Please ensure that {isCloud ? "the temporary password is correct and " : ""}the selected identification document file is valid
              </Trans>
            }
        /> }

      <Typography
        sx={{
          mb: 2,
          fontSize: "16px",
          fontWeight: "500",
        }}
      >
        {t("dropFileForm.headerText")}
      </Typography>

      {endpoints.isCloud ? dropFileFormCloud : null}

      <Typography
        sx={{
          fontSize: "14px",
        }}
      >
        {t("dropFileForm.uploadFile.title")}
      </Typography>
      <Typography
        sx={{
          mb: 1,
          fontSize: "12px",
          color: palette.text.secondary,
        }}
      >
        {t("dropFileForm.uploadFile.subtitle")}
      </Typography>
      <Box sx={{ cursor: "pointer" }}>
        <DragDropFileUpload onFileUpload={setUploadedFile} />
      </Box>
      <Stack sx={{ mt: 2 }}>
        <Button
          disabled={!dropFileButtonEnabled || isSubmitButtonLoading}
          sx={{ backgroundColor: "#1D82C0" }}
          onClick={() => loginAction(uploadedFile)}
          variant="contained"
        >
            {isSubmitButtonLoading ? <CircularProgress size={18} /> : t("button.guest")}
        </Button>
      </Stack>
    </Box>
  );

  const renderForm = () => {
    switch (formState) {
      case otpFormStates.Initial:
        return requestForm;
      case otpFormStates.OtpField:
        return otpForm;
      case otpFormStates.DropFile:
        return dropFileForm;
      default:
        return requestForm;
    }
  };

  return (
    <LoginPageWrapper
      spacing={4}
      showReturnButton
      onReturnChange={handleBackButton}
    >
      {renderForm()}
    </LoginPageWrapper>
  );
};
